import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import { FieldArray, Formik } from 'formik';
import FormField from 'components/FormField';
import { Button, Form, Input, message, Modal, ModalProps } from 'antd';
import { useMemoizedFn, useRequest } from 'ahooks';
import { css, cx } from '@emotion/css';
import { createOrUpdateTodoPolicy } from './api';
import { filter, isEmpty, unset, slice, get, sumBy, isArray, forEach, set } from 'lodash-es';
import useModal from 'hooks/useModal';

export interface TodoTypeSelfProps {
  initialData: {
    task_id: number;
    policy_id: number;
    typeName?: string;
    subTypes?: string[];
    source?: 0 | 1;
    subTypeTrees?: Todo.SubTypeTreeNode[];
    enableRefundOnly?: boolean;
    enableRefundGoods?: boolean;
  };
  onRefresh: () => void;
  existNames: string[];
}

interface TodoTypeProps extends ModalProps, TodoTypeSelfProps {}

// 为了恶心的重复校验，这里把官方的类型也加进来了
export const officialTypes = [
  '买家索要收据或发票',
  'Customer requests for invoice or receipt',
  'Customer meminta kuitansi/faktur',
  'ผู้ซื้อขอใบเสร็จรับเงินหรือใบแจ้งหนี้',
  'Customer request para sa invoice o resibo',
  'Người mua yêu cầu biên lai hoặc hóa đơn',
  'Comprador solicita recibo ou fatura',
  '商品无法使用',
  "Product doesn't work",
  'Hindi gumagana ang product',
  'Produk tidak dapat digunakan',
  'produto não pode ser utilizado',
  'สินค้าใช้งานไม่ได้',
  'Sản phẩm không dùng được',
  '咨询批发商品',
  'Inquire about wholesale',
  'Tanong tungkol sa wholesale products',
  'Konsultasi Produk Grosir',
  'Consultar produtos atacados',
  'สอบถามเกี่ยวกับสินค้าขายส่ง',
  'Tư vấn sản phẩm bán sỉ',
  '商品少发错发',
  'Product missing or wrong',
  'Produk salah/kurang',
  'ินค้าไม่ครบ/ส่งผิด',
  'Nawawala o maling product',
  'Gửi sai/thiếu hàng',
  'Falta e desvio de produto',
  '买家希望换货',
  'Customer requests for exchange',
  'Customer mengajukan penggantian produk',
  'ผู้ซื้อขอเปลี่ยนสินค้า',
  'Customer request para palitan',
  'Người mua muốn đổi hàng',
  'Comprador solicita trocar',
  '买家希望退款',
  'Customer requests for refund',
  'Customer mengajukan pengembalian dana',
  'ผู้ซื้อขอคืนเงิน',
  'Customer request na mag-refund',
  'Người mua muốn hoàn tiền',
  'Comprador solicita reembolso',
  '买家希望退货',
  'Customer requests for return',
  'Customer mengajukan pengembalian barang',
  'ผู้ซื้อขอคืนสินค้า',
  'Customer request para ibalik',
  'Người mua muốn trả hàng',
  'Comprador solicita devolução',
];

export const officialResultTypes = [
  'RECEIPT',
  'RESEND_GOODS',
  'OFFER_COMPENSATION',
  'REFUND',
  'REISSUE_GOODS',
  'PARTIAL_REFUND',
  'NON_PROBLEM',
  'INVOICE',
  'GOODS_NORMAL',
  'RETURN_GOODS_REFUND',
  'USE_METHOD_ERROR',
  'SUBMITTED_DISPUTE',
  'NO_DISPUTE',
  'NO_DISPUTE_TRADE_CLOSED',
  'DELIVERY_FAILED_RESEND',
  'REACHED',
  'COLLECTED',
  'CANCEL_OUT_STOCK',
  'DELIVERED',
  'APPEAL_SHOPEE',
  'DISPUTE_SUCCESS',
  'DISPUTE_FAIL',
  'REISSUE_GOODS_ASK_BUYER',
  'REISSUE_GOOD_ASK_BUYER_ADDRESS',
  'REISSUE_GOOD_GET_BUYER_ADDRESS',
  'REISSUE_GOOD_SENDED',
  'PARTIAL_REFUND_ASK_BUYER',
  'PARTIAL_REFUND_FINISH',
  'REFUND_ASK_BUYER',
  'REFUND_BUYER_REQ',
  'REFUND_FINISH',
  'ONLY_REFUND',
  'OFFER_COUPONS',
  'CONTACT_LOGISTICS',
  'REFUND_SUGGEST',
  'EXPEDITED_DELIVERY',
  'OUT_STOCK',
  'RUSH_LOGISTICS',
  'EXPEDITED_DELIVERY_TELL_BUYER',
  'OUT_STOCK_TELL_BUYER_CANCEL',
  'OUT_STOCK_ASK_BUYER_WAIT',
  'REACHED_LOGISTICS',
  'COLLECTED_TELL_RESULT',
  'CONTACT_LOGISTICS_SUGGEST',
  'REFUND_SUGGEST_BUYER',
  'RESEND_GOODS_ASK_BUYER',
  'RESEND_GOOD_ASK_BUYER_ADDRESS',
  'RESEND_GOOD_GET_BUYER_ADDRESS',
  'RESEND_GOOD_SENDED',
  'OFFER_COMPENSATION_ASK_BUYER',
  'OFFER_COMPENSATION_TELL_PROCESS',
  'REFUSE_RETURN_GOODS_ASK_BUYER',
  'REFUSE_RETURN_GOODS_TELL_PROCESS',
  'REFUSE_RETURN_GOODS_BUYER_REQ',
  'REFUSE_RETURN_GOODS_FINISH',
  'USE_METHOD_ERROR_TELL_METHOD',
  'OFFER_COUPONS_ASK_BUYER',
  'OFFER_COUPONS_SENT',
  'PARTIAL_REFUND_TELL_PROCESS',
  'REFUND_WAIT_REQ',
  'DELIVERED_ORDER',
  'RUSH_LOGISTICS_TELL_RESULT',
  'ASK_REASON',
  'REJECT_CANCEL',
  'AGREE_CANCEL',
  'NON_BUYER_CANCEL',
  'ORDER_CANCEL_ASK_BUYER',
  'ORDER_CANCEL_REJECT_DELIVERED',
  'ORDER_CANCEL_REJECT_REFUND',
  'ORDER_CANCEL_REJECT_OTHER',
  'ORDER_CANCEL_AGREE_SEND_APPLY',
  'ORDER_CANCEL_AGREE_APPLY_PROCESS',
  'ORDER_CANCEL_AGREE',
  'ORDER_CANCEL_TELL_REASON',
];

type FormikValue = {
  typeName: string;
  policy_id: number;
  subTypes?: string[];
  source: 0 | 1;
  subTypeTrees: Todo.SubTypeTreeNode[];
};

const TodoType = (props: TodoTypeProps) => {
  const { t } = useTranslate();
  const { initialData, existNames, onRefresh, ...restProps } = props;
  const { loading, runAsync } = useRequest((data) => createOrUpdateTodoPolicy(data), {
    manual: true,
  });
  const isCreate = !initialData.policy_id;
  const disabledName = initialData.source === 0;
  const countSubTypes = useMemoizedFn((obj: any) => {
    let count = 1;

    if (isArray(obj.subTypes)) {
      forEach(obj.subTypes, (child) => {
        count += countSubTypes(child);
      });
    }
    return count;
  });
  const countError = useMemoizedFn((obj: any) => {
    let count = 0;
    if (obj && Object.hasOwnProperty.call(obj, 'typeName')) {
      count += 1;
    }
    if (isArray(obj?.subTypes)) {
      forEach(obj?.subTypes, (child) => {
        count += countError(child);
      });
    }
    return count;
  });

  return (
    <Formik<FormikValue>
      initialValues={{
        typeName: '',
        ...props.initialData,
        subTypes: isEmpty(props.initialData.subTypes) ? [''] : props.initialData.subTypes,
        subTypeTrees: isEmpty(props.initialData.subTypeTrees)
          ? [{ typeName: '', subTypes: [], level: 0 }]
          : props.initialData.subTypeTrees!,
      }}
      enableReinitialize
      validateOnChange={false}
      validate={(values) => {
        const errors: any = {};
        values.subTypeTrees.forEach((tree, index) => {
          const name = get(tree, 'typeName', '');

          if (
            name &&
            filter(values.subTypeTrees, (o, i) => i !== index && o.typeName === name).length > 0
          ) {
            set(errors, `subTypeTrees.${index}.typeName`, t('todo_type_repeat_tip'));
          } else if (!name && filter(tree.subTypes, (o) => !!o.typeName).length > 0) {
            set(errors, `subTypeTrees.${index}.typeName`, t('请输入一级子类型名称'));
          }
          tree.subTypes.forEach((subType, index2) => {
            const name = get(subType, 'typeName', '');
            if (
              name &&
              filter(tree.subTypes, (o, i) => i !== index2 && o.typeName === name).length > 0
            ) {
              set(
                errors,
                `subTypeTrees.${index}.subTypes.${index2}.typeName`,
                t('todo_type_repeat_tip'),
              );
            } else if (!name && filter(tree.subTypes, (o) => !!o.typeName).length > 0) {
              set(
                errors,
                `subTypeTrees.${index}.subTypes.${index2}.typeName`,
                t('请输入二级子类型名称'),
              );
            }
            subType.subTypes.forEach((subType2, index3) => {
              const name = get(subType2, 'typeName', '');
              if (
                name &&
                filter(subType.subTypes, (o, i) => i !== index3 && o.typeName === name).length > 0
              ) {
                set(
                  errors,
                  `subTypeTrees.${index}.subTypes.${index2}.subTypes.${index3}.typeName`,
                  t('todo_type_repeat_tip'),
                );
              }
            });
          });
        });
        return errors;
      }}
      onSubmit={(values) => {
        if (values.source === 0) {
          unset(values, 'typeName');
        }
        values.subTypes = filter(values.subTypes, (o) => !!o);
        values.subTypeTrees = trimEmpty(values.subTypeTrees);
        runAsync({ ...initialData, ...values })
          .then(() => {
            onRefresh();
            message.success(t('operation.saveSucceed'));
            props.onCancel?.(null!);
          })
          .catch((err) => {
            console.log(err);
            message.error(t('operation.saveFailed'));
          });
      }}
    >
      {({ dirty, handleSubmit, values, errors }) => {
        return (
          <JustRightHeightModal
            width={500}
            centered
            title={isCreate ? t('todo_new_type') : t('marketing.编辑')}
            okButtonProps={{
              disabled: !dirty,
              loading,
            }}
            onOk={() => handleSubmit()}
            {...restProps}
            className={css`
              .ant-modal-body {
                padding: 16px !important;
              }
            `}
          >
            {isCreate && (
              <div className="leading-[20px] px-[10px] py-[6px] bg-[#f0f7ff] text-[#515359] mb-[12px]">
                {t('todo_new_type_tip')}
              </div>
            )}
            <Form layout="vertical">
              <FormField
                required
                name="typeName"
                label={t('todo_type_name')}
                validate={(value) => {
                  if (initialData.source === 0) return undefined;
                  if (!value) {
                    return t('todo_type_empty_tip');
                  }
                  if (existNames.includes(value) || officialTypes.includes(value)) {
                    return t('todo_type_repeat_tip');
                  }
                  return undefined;
                }}
              >
                {({ field }) => {
                  return (
                    <Input
                      disabled={disabledName}
                      maxLength={100}
                      showCount
                      value={field.value}
                      onChange={field.onChange}
                    />
                  );
                }}
              </FormField>

              <FieldArray name="subTypeTrees" shouldUpdate={() => true}>
                {(arrayHelpers) => {
                  return (
                    <Form.Item label={t('st_1')}>
                      <div
                        className={css`
                          .ant-form-item-explain-error {
                            padding-left: 22px;
                          }
                        `}
                      >
                        {values.subTypeTrees?.map((_, index) => {
                          return (
                            <FieldArray
                              name={`subTypeTrees.${index}.subTypes`}
                              key={index}
                              shouldUpdate={() => true}
                            >
                              {(arrayHelpers2) => {
                                const count = sumBy(
                                  slice(values.subTypeTrees[index].subTypes, 0, -1),
                                  countSubTypes,
                                );
                                const errorCount =
                                  sumBy(
                                    errors?.subTypeTrees?.[index]?.subTypes?.length !==
                                      values?.subTypeTrees?.[index]?.subTypes?.length
                                      ? errors?.subTypeTrees?.[index]?.subTypes
                                      : slice(errors?.subTypeTrees?.[index]?.subTypes!, 0, -1),
                                    countError,
                                  ) + (errors?.subTypeTrees?.[index]?.typeName ? 1 : 0);

                                return (
                                  <div
                                    key={index}
                                    className={cx(
                                      'bg-[#f7f9fa] p-[12px] !pb-[1px] mb-[16px] last:mb-0',
                                      css`
                                        position: relative;
                                        &:before {
                                          content: '';
                                          display: block;
                                          position: absolute;
                                          width: 1px;
                                          top: 43px;
                                          background: #d3d7e0;
                                          left: 20px;
                                          height: calc(
                                            44px *
                                              ${count +
                                              (get(values, `subTypeTrees.${index}.subTypes`, [])
                                                .length > 0
                                                ? 1
                                                : 0)} - 15px + 24px * ${errorCount}
                                          );
                                        }
                                      `,
                                    )}
                                  >
                                    <FormField
                                      name={`subTypeTrees.${index}.typeName`}
                                      className="!mb-[12px]"
                                      validateTrigger="onBlur"
                                    >
                                      {({ field }) => {
                                        return (
                                          <div className="flex items-center">
                                            <Input
                                              placeholder={t('请输入一级子类型名称')}
                                              onBlur={field.onBlur}
                                              value={field.value}
                                              onChange={field.onChange}
                                              showCount
                                              maxLength={100}
                                            />
                                            <Button
                                              className="ml-[8px] !px-[4px]"
                                              onClick={() => {
                                                arrayHelpers2.push({
                                                  typeName: '',
                                                  subTypes: [],
                                                  level: 1,
                                                });
                                              }}
                                              type="link"
                                            >
                                              {t('group.add')}
                                            </Button>
                                            <Button
                                              disabled={values.subTypeTrees.length === 1}
                                              className="!px-[4px]"
                                              onClick={() => {
                                                if (!!field.value) {
                                                  Modal.confirm({
                                                    title: t('official_delete_tip'),
                                                    onOk: () => {
                                                      arrayHelpers.remove(index);
                                                    },
                                                  });
                                                } else {
                                                  arrayHelpers.remove(index);
                                                }
                                              }}
                                              type="link"
                                            >
                                              {t('phrase.删除')}
                                            </Button>
                                          </div>
                                        );
                                      }}
                                    </FormField>
                                    <div className="ml-[24px]">
                                      {values.subTypeTrees[index].subTypes?.map((_, index2) => {
                                        return (
                                          <FieldArray
                                            name={`subTypeTrees.${index}.subTypes.${index2}.subTypes`}
                                            shouldUpdate={() => true}
                                          >
                                            {(arrayHelpers3) => {
                                              const count = get(
                                                values,
                                                `subTypeTrees.${index}.subTypes.${index2}.subTypes`,
                                                [],
                                              ).length;

                                              const errorCount =
                                                sumBy(
                                                  errors?.subTypeTrees?.[index]?.subTypes?.[index2]
                                                    ?.subTypes?.length !==
                                                    values?.subTypeTrees?.[index]?.subTypes?.[
                                                      index2
                                                    ]?.subTypes?.length
                                                    ? errors?.subTypeTrees?.[index]?.subTypes?.[
                                                        index2
                                                      ]?.subTypes!
                                                    : slice(
                                                        errors?.subTypeTrees?.[index]?.subTypes?.[
                                                          index2
                                                        ]?.subTypes!,
                                                        0,
                                                        -1,
                                                      ),
                                                  countError,
                                                ) +
                                                (errors?.subTypeTrees?.[index]?.subTypes?.[index2]
                                                  ?.typeName
                                                  ? 1
                                                  : 0);

                                              return (
                                                <div
                                                  key={index2}
                                                  className={css`
                                                    position: relative;
                                                    &:before {
                                                      content: '';
                                                      display: block;
                                                      position: absolute;
                                                      width: 1px;
                                                      top: 31px;
                                                      background: #d3d7e0;
                                                      left: 10px;
                                                      height: calc(
                                                        44px * ${count} - 15px + 24px *
                                                          ${errorCount}
                                                      );
                                                    }
                                                  `}
                                                >
                                                  <FormField
                                                    name={`subTypeTrees.${index}.subTypes.${index2}.typeName`}
                                                    className="!mb-[12px]"
                                                    validateTrigger="onBlur"
                                                    validate={(value: string) => {
                                                      return undefined;
                                                    }}
                                                  >
                                                    {({ field }) => {
                                                      return (
                                                        <div
                                                          className={cx(
                                                            'flex items-center',
                                                            css`
                                                              position: relative;
                                                              &:before {
                                                                content: '';
                                                                display: block;
                                                                position: absolute;
                                                                height: 1px;
                                                                width: 16px;
                                                                left: -16px;
                                                                background: #d3d7e0;
                                                              }
                                                            `,
                                                          )}
                                                        >
                                                          <Input
                                                            placeholder={t('请输入二级子类型名称')}
                                                            onBlur={field.onBlur}
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            showCount
                                                            maxLength={100}
                                                          />
                                                          <Button
                                                            className="ml-[8px] !px-[4px]"
                                                            onClick={() => {
                                                              arrayHelpers3.push({
                                                                typeName: '',
                                                                subTypes: [],
                                                                level: 2,
                                                              });
                                                            }}
                                                            type="link"
                                                          >
                                                            {t('group.add')}
                                                          </Button>
                                                          <Button
                                                            className="!px-[4px]"
                                                            onClick={() => {
                                                              if (!!field.value) {
                                                                Modal.confirm({
                                                                  title: t('official_delete_tip'),
                                                                  onOk: () => {
                                                                    arrayHelpers2.remove(index2);
                                                                  },
                                                                });
                                                              } else {
                                                                arrayHelpers2.remove(index2);
                                                              }
                                                            }}
                                                            type="link"
                                                          >
                                                            {t('phrase.删除')}
                                                          </Button>
                                                        </div>
                                                      );
                                                    }}
                                                  </FormField>
                                                  <div
                                                    className={cx(
                                                      'ml-[24px]',
                                                      css`
                                                        .ant-form-item-explain-error {
                                                          padding-left: 0 !important;
                                                        }
                                                      `,
                                                    )}
                                                  >
                                                    {values.subTypeTrees[index].subTypes[
                                                      index2
                                                    ].subTypes?.map((_, index3) => {
                                                      return (
                                                        <FormField
                                                          key={index3}
                                                          name={`subTypeTrees.${index}.subTypes.${index2}.subTypes.${index3}.typeName`}
                                                          className="!mb-[12px]"
                                                          validateTrigger="onBlur"
                                                          validate={(value: string) => {
                                                            return undefined;
                                                          }}
                                                        >
                                                          {({ field }) => {
                                                            return (
                                                              <div
                                                                className={cx(
                                                                  'flex items-center',
                                                                  css`
                                                                    position: relative;
                                                                    &:before {
                                                                      content: '';
                                                                      display: block;
                                                                      position: absolute;
                                                                      height: 1px;
                                                                      width: 14px;
                                                                      left: -14px;
                                                                      background: #d3d7e0;
                                                                    }
                                                                  `,
                                                                )}
                                                              >
                                                                <Input
                                                                  placeholder={t(
                                                                    '请输入三级子类型名称',
                                                                  )}
                                                                  onBlur={field.onBlur}
                                                                  value={field.value}
                                                                  onChange={field.onChange}
                                                                  showCount
                                                                  maxLength={100}
                                                                />
                                                                <Button
                                                                  className="ml-[8px] !px-[4px]"
                                                                  onClick={() => {
                                                                    if (!!field.value) {
                                                                      Modal.confirm({
                                                                        title:
                                                                          t('official_delete_tip'),
                                                                        onOk: () => {
                                                                          arrayHelpers3.remove(
                                                                            index3,
                                                                          );
                                                                        },
                                                                      });
                                                                    } else {
                                                                      arrayHelpers3.remove(index3);
                                                                    }
                                                                  }}
                                                                  type="link"
                                                                >
                                                                  {t('phrase.删除')}
                                                                </Button>
                                                              </div>
                                                            );
                                                          }}
                                                        </FormField>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              );
                                            }}
                                          </FieldArray>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              }}
                            </FieldArray>
                          );
                        })}
                      </div>
                      <Button
                        className="mt-[16px]"
                        type="dashed"
                        block
                        onClick={() => {
                          arrayHelpers.push({
                            typeName: '',
                            subTypes: [],
                            level: 0,
                          });
                        }}
                      >
                        + {t('添加一级子类型')}
                      </Button>
                    </Form.Item>
                  );
                }}
              </FieldArray>
            </Form>
          </JustRightHeightModal>
        );
      }}
    </Formik>
  );
};

type initialData = {
  typeName: string;
  policy_id: number;
  subTypes: string[];
  source: 0 | 1;
  subTypeTrees: Todo.SubTypeTreeNode[];
  enableRefundGoods?: boolean;
  enableRefundOnly?: boolean;
  enable?: boolean;
  enableManualCreate?: boolean;
  enableKolContactStore?: boolean;
  enableStoreContactKol?: boolean;
};

export const useTodoType = (props: {
  taskId?: number;
  refresh: () => void;
  existNames: string[];
}) => {
  const { visible, hide, show, meta } = useModal<Pick<TodoTypeSelfProps, 'initialData'>>();
  const open = useMemoizedFn((initialData?: initialData) => {
    show({
      initialData: {
        ...initialData,
        task_id: props.taskId!,
      },
    });
  });

  const instance = visible && props.taskId && meta && (
    <TodoType
      {...meta}
      visible={visible}
      onCancel={() => hide()}
      onRefresh={props.refresh}
      existNames={props.existNames}
    />
  );
  return [instance, open] as const;
};

// 裁剪没有填值的情况
const trimEmpty = (obj: Todo.SubTypeTreeNode[]) => {
  return filter(obj, (o) => {
    if (!isEmpty(o.subTypes)) {
      o.subTypes = trimEmpty(o.subTypes);
    }
    return !!o.typeName;
  });
};
